import React, { useState, useEffect, useCallback, JSX } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';

interface User {
  id: string;
  email: string;
  name: string;
  username: string;
  role: 'admin' | 'manager' | 'freelancer' | 'referrer' | 'client';
  is_approved: boolean;
  whatsapp_contact?: string;
  account_number?: string;
  bank_name?: string;
  account_name?: string;
  referrer_id?: string;
}

// Interface for project statistics
interface ProjectStats {
  totalProjects?: number;
  pendingProjects?: number;
  activeProjects?: number;
  inProgressProjects?: number;
  completedProjects?: number;
  cancelledProjects?: number;
  totalSpent?: number;
  averageBudget?: number;
  totalEarned?: number;
  potentialEarnings?: number;
  recentProjects?: number;
  pendingSpending?: number;
}

const Dashboard: React.FC<{}> = (): JSX.Element => {
  const navigate = useNavigate();
  const location = useLocation();
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [projectStats, setProjectStats] = useState<ProjectStats | null>(null);
  const [unreadNotifications, setUnreadNotifications] = useState(0);
  const [loadingStats, setLoadingStats] = useState(false);

  const loadUserData = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        navigate('/signin');
        return;
      }

      // Try to get fresh data from API
      const response = await axios.get('http://localhost:5000/api/users/profile', {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });

      if (response.data.success) {
        const userData = response.data.data.user;
        setUser(userData);
        localStorage.setItem('user', JSON.stringify(userData));
        
        // Load project stats based on user role
        await loadProjectStats(userData);
        
        // Load notifications
        await loadNotifications();
      } else {
        // Fallback to localStorage data
        const userData = localStorage.getItem('user');
        if (userData) {
          const parsedUser = JSON.parse(userData);
          setUser(parsedUser);
          await loadProjectStats(parsedUser);
        }
      }
    } catch (error) {
      console.error('Error loading user data:', error);
      // Fallback to localStorage data
      const userData = localStorage.getItem('user');
      if (userData) {
        const parsedUser = JSON.parse(userData);
        setUser(parsedUser);
      }
    } finally {
      setIsLoading(false);
    }
  }, [navigate]);

  const loadProjectStats = async (userData: User) => {
    try {
      setLoadingStats(true);
      const token = localStorage.getItem('token');
      
      // Only load stats for approved users with relevant roles
      if (!userData.is_approved || (userData.role !== 'client' && userData.role !== 'freelancer' && userData.role !== 'admin' && userData.role !== 'manager')) {
        setLoadingStats(false);
        return;
      }

      const response = await axios.get('http://localhost:5000/api/projects/stats', {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });

      if (response.data.success) {
        setProjectStats(response.data.data.stats);
      }
    } catch (error) {
      console.error('Error loading project stats:', error);
    } finally {
      setLoadingStats(false);
    }
  };

  const loadNotifications = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get('http://localhost:5000/api/projects/notifications?unread_only=true&limit=5', {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });

      if (response.data.success) {
        setUnreadNotifications(response.data.data.unreadCount || 0);
      }
    } catch (error) {
      console.error('Error loading notifications:', error);
    }
  };

  useEffect(() => {
    loadUserData();
  }, [loadUserData]);

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    navigate('/');
  };

  const handleRequestApproval = async () => {
    if (!user) return;

    try {
      const response = await axios.post(
        'http://localhost:5000/api/users/request-approval',
        {},
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`
          }
        }
      );

      if (response.data.success) {
        alert(response.data.message);
        // Reload user data to reflect any changes
        await loadUserData();
      }
    } catch (error: any) {
      console.error('Error requesting approval:', error);
      alert(error.response?.data?.message || 'Error sending approval request');
    }
  };

  const hasProfileSetup = user?.whatsapp_contact && 
    (user.role === 'admin' || user.role === 'manager' || user.role === 'client' || 
     (user.account_number && user.bank_name && user.account_name));

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const getRoleBadgeColor = (role: string) => {
    switch (role) {
      case 'admin': return 'role-admin';
      case 'manager': return 'role-manager';
      case 'freelancer': return 'role-freelancer';
      case 'referrer': return 'role-referrer';
      case 'client': return 'role-client';
      default: return 'role-admin';
    }
  };

  // Format currency
  const formatCurrency = (amount: number | undefined) => {
    if (!amount) return '₦0';
    return `₦${amount.toLocaleString()}`;
  };

  // Format number with commas
  const formatNumber = (num: number | undefined) => {
    if (!num) return '0';
    return num.toLocaleString();
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="loading-spinner mx-auto mb-4"></div>
          <p className="text-gray-600">Loading your dashboard...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="text-red-600 mb-4">❌ User not found</div>
          <button 
            onClick={() => navigate('/signin')}
            className="btn btn-primary"
          >
            Sign In
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Navigation */}
      <nav className="navbar">
        <div className="navbar-container">
          <div className="flex items-center gap-">
            <div className="navbar-brand">
              <div className="brand-logo">NS</div>
              <span className="brand-text">NexSync</span>
            </div>
            
            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center gap-6">
              <button 
                type="button"
                onClick={() => navigate('/dashboard')}
                className={`nav-link ${location.pathname === '/dashboard' ? 'nav-link-active' : ''}`}
                title="Dashboard"
              >
                Dashboard
              </button>
              <button 
                type="button"
                onClick={() => navigate('/profile')}
                className={`nav-link ${location.pathname === '/profile' ? 'nav-link-active' : ''}`}
                title="Profile"
              >
                Profile
              </button>
              
              {/* Notifications */}
              {user.is_approved && (
                <button 
                  type="button"
                  onClick={() => navigate('/notifications')}
                  className="nav-link relative"
                  title="Notifications"
                >
                  🔔
                  {unreadNotifications > 0 && (
                    <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                      {unreadNotifications > 9 ? '9+' : unreadNotifications}
                    </span>
                  )}
                </button>
              )}
              
              {(user.role === 'admin' || user.role === 'manager') && (
                <button 
                  type="button"
                  onClick={() => navigate(user.role === 'admin' ? '/admin' : '/manager')}
                  className={`nav-link ${(location.pathname === '/admin' || location.pathname === '/manager') ? 'nav-link-active' : ''}`}
                  title={user.role === 'admin' ? 'Admin Panel' : 'Manager Panel'}
                >
                  {user.role === 'admin' ? 'Admin Panel' : 'Manager Panel'}
                </button>
              )}
              
              {/* Project Management Links based on role */}
              {user.is_approved && user.role === 'client' && (
                <>
                  <button 
                    type="button"
                    onClick={() => navigate('/post-project')}
                    className={`nav-link ${location.pathname === '/post-project' ? 'nav-link-active' : ''}`}
                    title="Post Project"
                  >
                    Post Project
                  </button>
                  <button 
                    type="button"
                    onClick={() => navigate('/client/projects')}
                    className={`nav-link ${location.pathname === '/client/projects' ? 'nav-link-active' : ''}`}
                    title="My Projects"
                  >
                    My Projects
                  </button>
                </>
              )}
              
              {user.is_approved && user.role === 'freelancer' && (
                <>
                  <button 
                    type="button"
                    onClick={() => navigate('/projects')}
                    className={`nav-link ${location.pathname === '/projects' ? 'nav-link-active' : ''}`}
                    title="Browse Projects"
                  >
                    Browse Projects
                  </button>
                  <button 
                    type="button"
                    onClick={() => navigate('/freelancer/projects')}
                    className={`nav-link ${location.pathname === '/freelancer/projects' ? 'nav-link-active' : ''}`}
                    title="My Projects"
                  >
                    My Projects
                  </button>
                </>
              )}
            </div>
          </div>

          <div className="flex items-center gap-4">
            {/* User Info - Desktop */}
            <div className="hidden md:flex items-center gap-3">
              <div className="text-right">
                <div className="text-sm font-medium text-gray-900">{user.username}</div>
                <div className="flex items-center gap-2">
                  <span className={`role-badge ${getRoleBadgeColor(user.role)}`}>
                    {user.role}
                  </span>
                  <span className={`status-badge ${user.is_approved ? 'approved' : 'pending'}`}>
                    {user.is_approved ? 'Approved' : 'Pending'}
                  </span>
                </div>
              </div>
              <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center text-blue-600 font-bold">
                {user.username?.charAt(0).toUpperCase() || 'U'}
              </div>
            </div>

            {/* Mobile Menu Button */}
            <button 
              type="button"
              className="md:hidden p-2 rounded-md text-gray-600 hover:text-gray-900 hover:bg-gray-100"
              onClick={toggleMobileMenu}
              title="Toggle menu"
              aria-label="Toggle menu"
              aria-expanded={isMobileMenuOpen}
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                {isMobileMenuOpen ? (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                ) : (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                )}
              </svg>
            </button>

            {/* Logout Button - Desktop */}
            <button 
              type="button"
              onClick={handleLogout}
              className="hidden md:flex btn btn-ghost btn-sm"
              title="Logout"
            >
              <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
              </svg>
              Logout
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div className="md:hidden bg-white border-t border-gray-200">
            <div className="px-4 py-3 space-y-3">
              <button 
                type="button"
                onClick={() => {
                  navigate('/dashboard');
                  setIsMobileMenuOpen(false);
                }}
                className={`nav-link-mobile ${location.pathname === '/dashboard' ? 'nav-link-mobile-active' : ''}`}
                title="Dashboard"
              >
                <svg className="w-5 h-5 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
                </svg>
                Dashboard
              </button>
              
              <button 
                type="button"
                onClick={() => {
                  navigate('/profile');
                  setIsMobileMenuOpen(false);
                }}
                className={`nav-link-mobile ${location.pathname === '/profile' ? 'nav-link-mobile-active' : ''}`}
                title="Profile"
              >
                <svg className="w-5 h-5 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                </svg>
                Profile
              </button>

              {/* Notifications - Mobile */}
              {user.is_approved && (
                <button 
                  type="button"
                  onClick={() => {
                    navigate('/notifications');
                    setIsMobileMenuOpen(false);
                  }}
                  className="nav-link-mobile"
                  title="Notifications"
                >
                  <div className="flex items-center">
                    <svg className="w-5 h-5 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
                    </svg>
                    Notifications
                    {unreadNotifications > 0 && (
                      <span className="ml-auto bg-red-500 text-white text-xs rounded-full w-6 h-6 flex items-center justify-center">
                        {unreadNotifications > 9 ? '9+' : unreadNotifications}
                      </span>
                    )}
                  </div>
                </button>
              )}

              {(user.role === 'admin' || user.role === 'manager') && (
                <button 
                  type="button"
                  onClick={() => {
                    navigate(user.role === 'admin' ? '/admin' : '/manager');
                    setIsMobileMenuOpen(false);
                  }}
                  className={`nav-link-mobile ${(location.pathname === '/admin' || location.pathname === '/manager') ? 'nav-link-mobile-active' : ''}`}
                  title={user.role === 'admin' ? 'Admin Panel' : 'Manager Panel'}
                >
                  <svg className="w-5 h-5 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                  {user.role === 'admin' ? 'Admin Panel' : 'Manager Panel'}
                </button>
              )}

              {/* Project Management Links - Mobile */}
              {user.is_approved && user.role === 'client' && (
                <>
                  <button 
                    type="button"
                    onClick={() => {
                      navigate('/post-project');
                      setIsMobileMenuOpen(false);
                    }}
                    className={`nav-link-mobile ${location.pathname === '/post-project' ? 'nav-link-mobile-active' : ''}`}
                    title="Post Project"
                  >
                    <svg className="w-5 h-5 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                    </svg>
                    Post Project
                  </button>
                  <button 
                    type="button"
                    onClick={() => {
                      navigate('/client/projects');
                      setIsMobileMenuOpen(false);
                    }}
                    className={`nav-link-mobile ${location.pathname === '/client/projects' ? 'nav-link-mobile-active' : ''}`}
                    title="My Projects"
                  >
                    <svg className="w-5 h-5 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                    </svg>
                    My Projects
                  </button>
                </>
              )}
              
              {user.is_approved && user.role === 'freelancer' && (
                <>
                  <button 
                    type="button"
                    onClick={() => {
                      navigate('/projects');
                      setIsMobileMenuOpen(false);
                    }}
                    className={`nav-link-mobile ${location.pathname === '/projects' ? 'nav-link-mobile-active' : ''}`}
                    title="Browse Projects"
                  >
                    <svg className="w-5 h-5 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                    </svg>
                    Browse Projects
                  </button>
                  <button 
                    type="button"
                    onClick={() => {
                      navigate('/freelancer/projects');
                      setIsMobileMenuOpen(false);
                    }}
                    className={`nav-link-mobile ${location.pathname === '/freelancer/projects' ? 'nav-link-mobile-active' : ''}`}
                    title="My Projects"
                  >
                    <svg className="w-5 h-5 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                    </svg>
                    My Projects
                  </button>
                </>
              )}

              {/* Mobile User Info */}
              <div className="px-3 py-2 border-t border-gray-200 mt-3 pt-3">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center text-blue-600 font-bold">
                    {user.username?.charAt(0).toUpperCase() || 'U'}
                  </div>
                  <div className="flex-1">
                    <div className="text-sm font-medium text-gray-900">{user.username}</div>
                    <div className="flex items-center gap-2 mt-1">
                      <span className={`role-badge ${getRoleBadgeColor(user.role)}`}>
                        {user.role}
                      </span>
                      <span className={`status-badge ${user.is_approved ? 'approved' : 'pending'}`}>
                        {user.is_approved ? 'Approved' : 'Pending'}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Mobile Logout */}
              <button 
                type="button"
                onClick={() => {
                  handleLogout();
                  setIsMobileMenuOpen(false);
                }}
                className="nav-link-mobile text-red-600 hover:text-red-700 hover:bg-red-50"
                title="Logout"
              >
                <svg className="w-5 h-5 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                </svg>
                Logout
              </button>
            </div>
          </div>
        )}
      </nav>

      {/* Dashboard Content */}
      <div className="dashboard-container">
        <div className="dashboard-content">
          {/* Welcome Header */}
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-gray-900 mb-2">
              Welcome back, {user.username}!
            </h1>
            <p className="text-gray-600">
              Here's what's happening with your account today.
            </p>
          </div>

          {/* Profile Setup Reminder */}
          {!hasProfileSetup && (
            <div className="setup-reminder mb-6">
              <div className="warning-message">
                <div className="flex items-start gap-3">
                  <svg className="w-5 h-5 text-yellow-600 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                  </svg>
                  <div>
                    <h3 className="text-lg font-semibold text-yellow-800 mb-1">Profile Setup Required</h3>
                    <p className="text-yellow-700 mb-3">
                      Please complete your profile setup to access all features and request approval.
                    </p>
                    <button 
                      type="button"
                      onClick={() => navigate('/profile')}
                      className="btn btn-primary btn-sm"
                      title="Complete Profile Setup"
                    >
                      Complete Profile Setup
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Approval Request for Non-Admin Users */}
          {user.role !== 'admin' && !user.is_approved && (
            <div className="approval-section mb-6">
              <div className="info-message">
                <div className="flex items-start gap-3">
                  <svg className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                  </svg>
                  <div>
                    <h3 className="text-lg font-semibold text-blue-800 mb-1">
                      {user.role === 'freelancer' ? 'Work Approval Required' : 
                       user.role === 'referrer' ? 'View Access Required' : 
                       user.role === 'client' ? 'Client Access Required' : 'Manager Approval Required'}
                    </h3>
                    <p className="text-blue-700 mb-3">
                      {user.role === 'freelancer' 
                        ? 'You need approval to start working on projects and earning.' 
                        : user.role === 'referrer'
                        ? 'You need approval to view project progress and referral status.'
                        : user.role === 'client'
                        ? 'You need approval to post projects and hire freelancers.'
                        : 'You need approval to access manager features and tools.'
                      }
                    </p>
                    <button 
                      type="button"
                      onClick={handleRequestApproval}
                      className="btn btn-primary btn-sm"
                      disabled={!hasProfileSetup}
                      title={
                        user.role === 'freelancer' 
                          ? 'Request Work Approval'
                          : user.role === 'referrer'
                          ? 'Request View Access'
                          : user.role === 'client'
                          ? 'Request Client Access'
                          : 'Request Manager Approval'
                      }
                    >
                      {user.role === 'freelancer' 
                        ? 'Request Work Approval'
                        : user.role === 'referrer'
                        ? 'Request View Access'
                        : user.role === 'client'
                        ? 'Request Client Access'
                        : 'Request Manager Approval'
                      }
                    </button>
                    {!hasProfileSetup && (
                      <p className="text-blue-600 text-sm mt-2">
                        Complete your profile setup first to request approval
                      </p>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Project Statistics */}
          {user.is_approved && projectStats && (user.role === 'client' || user.role === 'freelancer' || user.role === 'admin' || user.role === 'manager') && (
            <div className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Project Statistics</h2>
              {loadingStats ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {[1, 2, 3].map(i => (
                    <div key={i} className="dashboard-card animate-pulse">
                      <div className="h-4 bg-gray-200 rounded w-3/4 mb-4"></div>
                      <div className="h-8 bg-gray-200 rounded w-1/2"></div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {user.role === 'client' && (
                    <>
                      <div className="dashboard-card">
                        <h3 className="text-lg font-semibold text-gray-900 mb-2">Total Projects</h3>
                        <div className="text-3xl font-bold text-blue-600">
                          {formatNumber(projectStats.totalProjects)}
                        </div>
                        <p className="text-gray-600 text-sm mt-2">Projects posted by you</p>
                      </div>
                      <div className="dashboard-card">
                        <h3 className="text-lg font-semibold text-gray-900 mb-2">Total Spent</h3>
                        <div className="text-3xl font-bold text-green-600">
                          {formatCurrency(projectStats.totalSpent)}
                        </div>
                        <p className="text-gray-600 text-sm mt-2">On completed projects</p>
                      </div>
                      <div className="dashboard-card">
                        <h3 className="text-lg font-semibold text-gray-900 mb-2">Active Projects</h3>
                        <div className="text-3xl font-bold text-orange-600">
                          {formatNumber(projectStats.activeProjects)}
                        </div>
                        <p className="text-gray-600 text-sm mt-2">Currently in progress</p>
                      </div>
                    </>
                  )}
                  
                  {user.role === 'freelancer' && (
                    <>
                      <div className="dashboard-card">
                        <h3 className="text-lg font-semibold text-gray-900 mb-2">Total Earnings</h3>
                        <div className="text-3xl font-bold text-green-600">
                          {formatCurrency(projectStats.totalEarned)}
                        </div>
                        <p className="text-gray-600 text-sm mt-2">From completed projects</p>
                      </div>
                      <div className="dashboard-card">
                        <h3 className="text-lg font-semibold text-gray-900 mb-2">Active Projects</h3>
                        <div className="text-3xl font-bold text-blue-600">
                          {formatNumber(projectStats.activeProjects)}
                        </div>
                        <p className="text-gray-600 text-sm mt-2">Currently working on</p>
                      </div>
                      <div className="dashboard-card">
                        <h3 className="text-lg font-semibold text-gray-900 mb-2">Potential Earnings</h3>
                        <div className="text-3xl font-bold text-purple-600">
                          {formatCurrency(projectStats.potentialEarnings)}
                        </div>
                        <p className="text-gray-600 text-sm mt-2">From active projects</p>
                      </div>
                    </>
                  )}
                  
                  {(user.role === 'admin' || user.role === 'manager') && (
                    <>
                      <div className="dashboard-card">
                        <h3 className="text-lg font-semibold text-gray-900 mb-2">Total Projects</h3>
                        <div className="text-3xl font-bold text-blue-600">
                          {formatNumber(projectStats.totalProjects)}
                        </div>
                        <p className="text-gray-600 text-sm mt-2">Across the platform</p>
                      </div>
                      <div className="dashboard-card">
                        <h3 className="text-lg font-semibold text-gray-900 mb-2">Pending Approval</h3>
                        <div className="text-3xl font-bold text-yellow-600">
                          {formatNumber(projectStats.pendingProjects)}
                        </div>
                        <p className="text-gray-600 text-sm mt-2">Waiting for freelancer assignment</p>
                      </div>
                      <div className="dashboard-card">
                        <h3 className="text-lg font-semibold text-gray-900 mb-2">Completed Projects</h3>
                        <div className="text-3xl font-bold text-green-600">
                          {formatNumber(projectStats.completedProjects)}
                        </div>
                        <p className="text-gray-600 text-sm mt-2">Successfully delivered</p>
                      </div>
                    </>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Dashboard Cards */}
          <div className="dashboard-cards">
            {user.is_approved ? (
              <>
                {/* Approved Users see their role-specific content */}
                <div className="dashboard-card">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                      <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900">Account Status</h3>
                  </div>
                  <p className="status-approved text-lg mb-3">✅ Approved</p>
                  <p className="text-gray-600">
                    Your account is fully activated. You have access to all features for your role.
                  </p>
                </div>

                {user.role === 'freelancer' && (
                  <>
                    <div className="dashboard-card">
                      <div className="flex items-center gap-3 mb-4">
                        <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                          <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                          </svg>
                        </div>
                        <h3 className="text-lg font-semibold text-gray-900">Browse Projects</h3>
                      </div>
                      <p className="text-gray-600 mb-4">
                        Find and request projects that match your skills and expertise.
                      </p>
                      <button 
                        type="button"
                        onClick={() => navigate('/projects')} 
                        className="btn btn-primary"
                        title="Browse Projects"
                      >
                        Browse Projects
                      </button>
                    </div>

                    <div className="dashboard-card">
                      <div className="flex items-center gap-3 mb-4">
                        <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                          <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                          </svg>
                        </div>
                        <h3 className="text-lg font-semibold text-gray-900">My Projects</h3>
                      </div>
                      <p className="text-gray-600 mb-4">
                        View projects assigned to you and manage your work.
                      </p>
                      <button 
                        type="button"
                        onClick={() => navigate('/freelancer/projects')} 
                        className="btn btn-primary"
                        title="My Projects"
                      >
                        My Projects
                      </button>
                    </div>

                    <div className="dashboard-card">
                      <div className="flex items-center gap-3 mb-4">
                        <div className="w-10 h-10 bg-yellow-100 rounded-lg flex items-center justify-center">
                          <svg className="w-6 h-6 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
                          </svg>
                        </div>
                        <h3 className="text-lg font-semibold text-gray-900">My Requests</h3>
                      </div>
                      <p className="text-gray-600 mb-4">
                        Track the status of your project requests and applications.
                      </p>
                      <button 
                        type="button"
                        onClick={() => navigate('/freelancer/requests')} 
                        className="btn btn-primary"
                        title="My Requests"
                      >
                        My Requests
                      </button>
                    </div>
                  </>
                )}

                {user.role === 'referrer' && (
                  <div className="dashboard-card">
                    <div className="flex items-center gap-3 mb-4">
                      <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                        <svg className="w-6 h-6 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                        </svg>
                      </div>
                      <h3 className="text-lg font-semibold text-gray-900">Project Tracking</h3>
                    </div>
                    <p className="text-gray-600 mb-4">
                      Monitor progress and status of projects you've referred.
                    </p>
                    <button 
                      type="button"
                      className="btn btn-primary" 
                      disabled
                      title="Coming Soon"
                    >
                      View Progress (Coming Soon)
                    </button>
                  </div>
                )}

                {user.role === 'client' && (
                  <>
                    <div className="dashboard-card">
                      <div className="flex items-center gap-3 mb-4">
                        <div className="w-10 h-10 bg-cyan-100 rounded-lg flex items-center justify-center">
                          <svg className="w-6 h-6 text-cyan-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                          </svg>
                        </div>
                        <h3 className="text-lg font-semibold text-gray-900">Post Projects</h3>
                      </div>
                      <p className="text-gray-600 mb-4">
                        Create and manage projects to find the right freelancers for your needs.
                      </p>
                      <button 
                        type="button"
                        onClick={() => navigate('/post-project')} 
                        className="btn btn-primary"
                        title="Post Project"
                      >
                        Post Project
                      </button>
                    </div>

                    <div className="dashboard-card">
                      <div className="flex items-center gap-3 mb-4">
                        <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                          <svg className="w-6 h-6 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                          </svg>
                        </div>
                        <h3 className="text-lg font-semibold text-gray-900">My Projects</h3>
                      </div>
                      <p className="text-gray-600 mb-4">
                        View and manage all your posted projects and track their progress.
                      </p>
                      <button 
                        type="button"
                        onClick={() => navigate('/client/projects')} 
                        className="btn btn-primary"
                        title="My Projects"
                      >
                        My Projects
                      </button>
                    </div>

                    <div className="dashboard-card">
                      <div className="flex items-center gap-3 mb-4">
                        <div className="w-10 h-10 bg-indigo-100 rounded-lg flex items-center justify-center">
                          <svg className="w-6 h-6 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                          </svg>
                        </div>
                        <h3 className="text-lg font-semibold text-gray-900">Freelancer Requests</h3>
                      </div>
                      <p className="text-gray-600 mb-4">
                        Review freelancer requests for your projects and choose the best fit.
                      </p>
                      <button 
                        type="button"
                        onClick={() => navigate('/client/requests')} 
                        className="btn btn-primary"
                        title="View Requests"
                      >
                        View Requests
                      </button>
                    </div>
                  </>
                )}

                {(user.role === 'admin' || user.role === 'manager') && (
                  <>
                    <div className="dashboard-card">
                      <div className="flex items-center gap-3 mb-4">
                        <div className="w-10 h-10 bg-indigo-100 rounded-lg flex items-center justify-center">
                          <svg className="w-6 h-6 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                          </svg>
                        </div>
                        <h3 className="text-lg font-semibold text-gray-900">
                          {user.role === 'admin' ? 'Admin Panel' : 'Manager Panel'}
                        </h3>
                      </div>
                      <p className="text-gray-600 mb-4">
                        Manage users, projects, approvals, and system settings.
                      </p>
                      <button 
                        type="button"
                        onClick={() => navigate(user.role === 'admin' ? '/admin' : '/manager')} 
                        className="btn btn-primary"
                        title={user.role === 'admin' ? 'Go to Admin Panel' : 'Go to Manager Panel'}
                      >
                        {user.role === 'admin' ? 'Go to Admin Panel' : 'Go to Manager Panel'}
                      </button>
                    </div>

                    <div className="dashboard-card">
                      <div className="flex items-center gap-3 mb-4">
                        <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                          <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                          </svg>
                        </div>
                        <h3 className="text-lg font-semibold text-gray-900">All Projects</h3>
                      </div>
                      <p className="text-gray-600 mb-4">
                        View and manage all projects across the platform.
                      </p>
                      <button 
                        type="button"
                        onClick={() => navigate('/admin/projects')} 
                        className="btn btn-primary"
                        title="Manage Projects"
                      >
                        Manage Projects
                      </button>
                    </div>

                    <div className="dashboard-card">
                      <div className="flex items-center gap-3 mb-4">
                        <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                          <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5 6v-.5a2.5 2.5 0 00-5 0v.5m-7 0v-.5a2.5 2.5 0 00-5 0v.5" />
                          </svg>
                        </div>
                        <h3 className="text-lg font-semibold text-gray-900">User Management</h3>
                      </div>
                      <p className="text-gray-600 mb-4">
                        Approve users, manage roles, and monitor user activity.
                      </p>
                      <button 
                        type="button"
                        onClick={() => navigate('/admin/users')} 
                        className="btn btn-primary"
                        title="Manage Users"
                      >
                        Manage Users
                      </button>
                    </div>
                  </>
                )}
              </>
            ) : user.role !== 'admin' ? (
              /* Waiting for approval */
              <div className="dashboard-card">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 bg-yellow-100 rounded-lg flex items-center justify-center">
                    <svg className="w-6 h-6 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900">Account Status</h3>
                </div>
                <p className="status-pending text-lg mb-3">⏳ Pending Approval</p>
                <p className="text-gray-600">
                  Your account is waiting for administrator approval. You'll receive notification once approved.
                </p>
              </div>
            ) : (
              /* Admin user */
              <div className="dashboard-card">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 bg-indigo-100 rounded-lg flex items-center justify-center">
                    <svg className="w-6 h-6 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900">Admin Dashboard</h3>
                </div>
                <p className="text-gray-600 mb-4">
                  Welcome to the administrator panel. Manage users, projects, and system settings.
                </p>
                <button 
                  type="button"
                  onClick={() => navigate('/admin')} 
                  className="btn btn-primary"
                  title="Go to Admin Panel"
                >
                  Go to Admin Panel
                </button>
              </div>
            )}
          </div>

          {/* Quick Actions */}
          {user.is_approved && (
            <div className="mt-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Quick Actions</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {/* Notifications Quick Action */}
                <button 
                  onClick={() => navigate('/notifications')}
                  className="dashboard-card hover:shadow-lg transition-shadow cursor-pointer"
                >
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                      <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
                      </svg>
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900">Notifications</h3>
                  </div>
                  <p className="text-gray-600">
                    {unreadNotifications > 0 
                      ? `You have ${unreadNotifications} unread notification${unreadNotifications > 1 ? 's' : ''}` 
                      : 'View your notifications'}
                  </p>
                  <div className="mt-4 text-blue-600 font-medium flex items-center">
                    View Notifications
                    <svg className="w-4 h-4 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
                    </svg>
                  </div>
                </button>

                {/* Profile Quick Action */}
                <button 
                  onClick={() => navigate('/profile')}
                  className="dashboard-card hover:shadow-lg transition-shadow cursor-pointer"
                >
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                      <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                      </svg>
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900">Profile</h3>
                  </div>
                  <p className="text-gray-600">
                    Update your profile information, contact details, and preferences.
                  </p>
                  <div className="mt-4 text-green-600 font-medium flex items-center">
                    Edit Profile
                    <svg className="w-4 h-4 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
                    </svg>
                  </div>
                </button>

                {/* Help & Support Quick Action */}
                <button 
                  onClick={() => navigate('/helpcenter')}
                  className="dashboard-card hover:shadow-lg transition-shadow cursor-pointer"
                >
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                      <svg className="w-6 h-6 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900">Help & Support</h3>
                  </div>
                  <p className="text-gray-600">
                    Get help, read documentation, or contact support for assistance.
                  </p>
                  <div className="mt-4 text-purple-600 font-medium flex items-center">
                    Get Help
                    <svg className="w-4 h-4 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
                    </svg>
                  </div>
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Dashboard;